import React, { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

// ─── Lazy feature imports (code splitting) ────────────────────────────────────

const AuthPage = lazy(() => import("@/features/auth/AuthPage").then(m => ({ default: m.AuthPage })));
const MapPage = lazy(() => import("@/features/map/MapPage").then(m => ({ default: m.MapPage })));
const MissionDetailPage = lazy(() => import("@/features/mission/MissionDetailPage").then(m => ({ default: m.MissionDetailPage })));
const ProfilePage = lazy(() => import("@/features/profile/ProfilePage").then(m => ({ default: m.ProfilePage })));
const WalletPage = lazy(() => import("@/features/wallet/WalletPage").then(m => ({ default: m.WalletPage })));
const NotificationsPage = lazy(() => import("@/features/notifications/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const OnboardingFlow = lazy(() => import("@/features/onboarding/OnboardingFlow").then(m => ({ default: m.OnboardingFlow })));
const PhotographerDashboard = lazy(() => import("@/features/photographer/components/PhotographerDashboard").then(m => ({ default: m.PhotographerDashboard })));
const CreateMissionWizard = lazy(() => import("@/features/missions/components/CreateMissionWizard").then(m => ({ default: m.CreateMissionWizard })));

// Shared layout (loaded eagerly — small, always needed)
import { BottomNav } from "@/shared/components/BottomNav";
import { SafeArea } from "@/shared/components/SafeArea";
import { AdminLayout } from "@/features/admin/AdminLayout";

// Optic Insights
const OpticInsightsDashboard = lazy(() => import("@/features/optic-insights/OpticInsightsDashboard").then(m => ({ default: m.OpticInsightsDashboard })));
const AlertDetailView = lazy(() => import("@/features/optic-insights/AlertDetailView").then(m => ({ default: m.AlertDetailView })));
const MissionCreatorPage = lazy(() => import("@/features/optic-insights/MissionCreatorPage").then(m => ({ default: m.MissionCreatorPage })));
const BillingPage = lazy(() => import("@/features/optic-insights/BillingPage").then(m => ({ default: m.BillingPage })));
const SubmissionsReview = lazy(() => import("@/features/optic-insights/components/SubmissionsReview").then(m => ({ default: m.SubmissionsReview })));

// Billing
const BillingDashboard = lazy(() => import("@/features/billing/BillingDashboard").then(m => ({ default: m.BillingDashboard })));
const CreditsPurchasePage = lazy(() => import("@/features/billing/CreditsPurchasePage").then(m => ({ default: m.CreditsPurchasePage })));
const RevenueReportPage = lazy(() => import("@/features/billing/RevenueReportPage").then(m => ({ default: m.RevenueReportPage })));
const InvoiceList = lazy(() => import("@/features/billing/InvoiceList").then(m => ({ default: m.InvoiceList })));
const SubscriptionPlans = lazy(() => import("@/features/billing/SubscriptionPlans").then(m => ({ default: m.SubscriptionPlans })));

// Ads
const AdsMarketplacePage = lazy(() => import("@/features/ads/AdsMarketplacePage").then(m => ({ default: m.AdsMarketplacePage })));
const CampaignDashboard = lazy(() => import("@/features/ads/CampaignDashboard").then(m => ({ default: m.CampaignDashboard })));
const CreateCampaignPage = lazy(() => import("@/features/ads/CreateCampaignPage").then(m => ({ default: m.CreateCampaignPage })));
const CampaignDetailPage = lazy(() => import("@/features/ads/CampaignDetailPage").then(m => ({ default: m.CampaignDetailPage })));
const BrandProfilePage = lazy(() => import("@/features/ads/BrandProfilePage").then(m => ({ default: m.BrandProfilePage })));

// Admin
const AdminDashboard = lazy(() => import("@/features/admin/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const PhotographerManagement = lazy(() => import("@/features/admin/PhotographerManagement").then(m => ({ default: m.PhotographerManagement })));
const MissionManagement = lazy(() => import("@/features/admin/MissionManagement").then(m => ({ default: m.MissionManagement })));
const ImageReview = lazy(() => import("@/features/admin/ImageReview").then(m => ({ default: m.ImageReview })));
const PayoutManagement = lazy(() => import("@/features/admin/PayoutManagement").then(m => ({ default: m.PayoutManagement })));
const ClientManagement = lazy(() => import("@/features/admin/ClientManagement").then(m => ({ default: m.ClientManagement })));

// ─── Suspense fallback ────────────────────────────────────────────────────────

function PageSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-ios-bg">
      <div className="w-8 h-8 border-2 border-ios-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// ─── Onboarding check ────────────────────────────────────────────────────────

/**
 * Returns true if the user has completed onboarding.
 * Uses onboarding_completed flag, with full_name as fallback.
 */
async function hasCompletedOnboarding(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from("profiles")
    .select("onboarding_completed, full_name")
    .eq("id", userId)
    .maybeSingle();

  return !!(data?.onboarding_completed || data?.full_name);
}

// ─── Protected layout ─────────────────────────────────────────────────────────

function ProtectedLayout() {
  return (
    <SafeArea>
      <div className="flex flex-col h-full">
        <main className="flex-1 overflow-hidden">
          <Suspense fallback={<PageSpinner />}>
            <Routes>
              <Route path="home" element={<PhotographerDashboard />} />
              <Route path="map" element={<MapPage />} />
              <Route path="mission/:id" element={<MissionDetailPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="wallet" element={<WalletPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="create-mission" element={<CreateMissionWizard />} />
              <Route path="*" element={<Navigate to="home" replace />} />
            </Routes>
          </Suspense>
        </main>
        <BottomNav />
      </div>
    </SafeArea>
  );
}

// ─── Auth guard with onboarding redirect ─────────────────────────────────────

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  useEffect(() => {
    if (!user || isLoading) return;

    hasCompletedOnboarding(user.id).then((done) => {
      setOnboardingChecked(true);
      if (!done) {
        navigate("/onboarding", { replace: true });
      }
    });
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return <PageSpinner />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Wait for onboarding check before rendering app
  if (!onboardingChecked) {
    return <PageSpinner />;
  }

  return <>{children}</>;
}

// ─── Onboarding guard (only accessible when NOT onboarded) ───────────────────

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <PageSpinner />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-base">
        <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check admin role from Supabase user_metadata
  const isAdmin =
    user.user_metadata?.role === "admin" ||
    user.app_metadata?.role === "admin";

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      <Route
        path="/app/*"
        element={
          <AuthGuard>
            <ProtectedLayout />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/*"
        element={
          <AdminGuard>
            <AdminLayout />
          </AdminGuard>
        }
      />
      <Route path="*" element={<Navigate to="/app/map" replace />} />
    </Routes>
  );
}
